/**
 * Cable Service (WebSocket)
 * 
 * Updated to use new session management architecture.
 * Issue: #202
 */

import { Injectable, OnDestroy } from '@angular/core'
import { ActionCableService, Channel, Cable } from 'angular2-actioncable'
import { Observable, Subject, Subscription, timer, fromEvent } from 'rxjs'
import { map, tap, filter, take, debounceTime } from 'rxjs/operators'

import { AuthenticationService } from '@/services/authentication.service'
import { TokenRefreshOrchestrator, RefreshSource } from '@/services/token-refresh-orchestrator.service'
import { SessionHealthMonitor } from '@/services/session-health-monitor.service'
import { ErrorClassifierService } from '@/services/error-classifier.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { log, warn, error } from '@/util/logger'

import { PaymentInitiationMessage } from '@/domain/payment-initiation-message'
import { Product } from '@/domain/product'
import { ProductBrand } from '@/domain/product-brand'
import { ProductType } from '@/domain/product-type'
import { ProductSize } from '@/domain/product-size'
import { Sale } from '@/domain/sale'
import { SaleRow } from '@/domain/sale-row'
import { SalesPeriod } from '@/domain/sales-period'
import { Seller } from '@/domain/seller'
import { Settlement } from '@/domain/settlement'

const PAYMENT_INITIATION_MESSAGE_CHANNEL = 'PaymentInitiationMessageChannel'
const PRODUCT_CHANNEL = 'ProductChannel'
const PRODUCT_BRAND_CHANNEL = 'ProductBrandChannel'
const PRODUCT_SIZE_CHANNEL = 'ProductSizeChannel'
const PRODUCT_TYPE_CHANNEL = 'ProductTypeChannel'
const SALE_CHANNEL = 'SaleChannel'
const SALE_ROW_CHANNEL = 'SaleRowChannel'
const SALES_PERIOD_CHANNEL = 'SalesPeriodChannel'
const SELLER_CHANNEL = 'SellerChannel'
const SETTLEMENT_CHANNEL = 'SettlementChannel'

// Connection state machine - explicit states instead of scattered boolean flags
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

// Reasons for reconnection - determines how we handle the reconnect
type ReconnectReason = 'auth_failure' | 'disconnected' | 'token_change' | 'connection_timeout'

@Injectable({ providedIn: 'root' })
export class CableService implements OnDestroy {

  private _cable: Cable = null
  private _reconnectTimer: Subscription = null
  private _tokenSubscription: Subscription = null
  private _tokenRefreshTimer: Subscription = null
  private _sessionStateSubscription: Subscription = null
  private _reconnectAttempts = 0
  private _maxReconnectAttempts = 5
  private _cableReady = new Subject<Cable>()
  private _periodicRefreshFailures = 0
  private _maxPeriodicRefreshFailures = 3

  // Connection state machine - replaces scattered boolean flags
  private _state: ConnectionState = 'disconnected'
  private _connectedChannelCount = 0
  private _connectionTimeout: any = null  // Safety timeout handle

  private _paymentInitiationMessageChannel: Channel = null
  private _productChannel: Channel = null
  private _productBrandChannel: Channel = null
  private _productSizeChannel: Channel = null
  private _productTypeChannel: Channel = null
  private _salesPeriodChannel: Channel = null
  private _saleChannel: Channel = null
  private _saleRowChannel: Channel = null
  private _sellerChannel: Channel = null
  private _settlementChannel: Channel = null

  private _channelSubjects = new Map<string, Subject<any>>()
  private _channelForwardSubscriptions = new Map<string, Subscription>()
  private _observedChannels = new Set<string>()
  private _isCleaningUp = false // Prevent operations during cleanup
  private _isAuthFailureReconnecting = false // Prevent dual-path race between auth-failure reconnect and tokenUpdate$

  constructor(
    public _auth: AuthenticationService,
    private _cableService: ActionCableService,
    private _refreshOrchestrator: TokenRefreshOrchestrator,
    private _healthMonitor: SessionHealthMonitor,
    private _errorClassifier: ErrorClassifierService,
    private _sessionState: SessionStateService
  ) {
    this._setupTokenChangeListener()
    this._setupVisibilityChangeListener()
    this._setupPeriodicTokenRefresh()
    this._setupSessionStateListener()

    // Update health monitor with WebSocket status
    this._healthMonitor.updateWebSocketStatus(false) // Initially disconnected
  }

  private _debug(channel, item): void {
    // console.log(channel + ': received cable', item)
  }

  // State machine transition with logging
  private _transitionTo(newState: ConnectionState): void {
    if (this._state === newState) return
    log(`[CableService] State: ${this._state} → ${newState}`)
    this._state = newState
  }

  get salesPeriods(): Observable<SalesPeriod> {
    return this._getChannelStream(SALES_PERIOD_CHANNEL).pipe(map(json => {
      const p = SalesPeriod.from(json)
      return p
    }))
  }

  get paymentInitiationMessages(): Observable<PaymentInitiationMessage> {
    return this._getChannelStream(PAYMENT_INITIATION_MESSAGE_CHANNEL).pipe(
      tap(x => { this._debug('paymentInitiationMessages', x) }),
      map(json => PaymentInitiationMessage.from(json))
    )
  }

  get products(): Observable<Product> {
    return this._getChannelStream(PRODUCT_CHANNEL).pipe(
      tap(x => { this._debug('products', x) }),
      map(string => Product.from(JSON.parse(string)))
    )
  }

  get productBrands(): Observable<ProductBrand> {
    return this._getChannelStream(PRODUCT_BRAND_CHANNEL).pipe(
      tap(x => { this._debug('productBrands', x) }),
      map(obj => ProductBrand.from(obj))
    )
  }

  get productTypes(): Observable<ProductType> {
    return this._getChannelStream(PRODUCT_TYPE_CHANNEL).pipe(
      tap(x => { this._debug('productTypes', x) }),
      map(obj => ProductType.from(obj))
    )
  }

  get productSizes(): Observable<ProductSize> {
    return this._getChannelStream(PRODUCT_SIZE_CHANNEL).pipe(
      tap(x => { this._debug('productSizes', x) }),
      map(obj => ProductSize.from(obj))
    )
  }

  get sales(): Observable<Sale> {
    return this._getChannelStream(SALE_CHANNEL).pipe(
      tap(x => { this._debug('sales', x) }),
      map(string => Sale.from(JSON.parse(string)))
    )
  }

  get saleRows(): Observable<SaleRow> {
    return this._getChannelStream(SALE_ROW_CHANNEL).pipe(
      tap(x => { this._debug('saleRows', x) }),
      map(string => SaleRow.from(JSON.parse(string)))
    )
  }

  get sellers(): Observable<Seller> {
    return this._getChannelStream(SELLER_CHANNEL).pipe(
      tap(x => { this._debug('sellers', x) }),
      map(string => Seller.from(JSON.parse(string)))
    )
  }

  get settlements(): Observable<Settlement> {
    return this._getChannelStream(SETTLEMENT_CHANNEL).pipe(
      tap(x => { this._debug('settlements', x) }),
      map(string => {
        const s = Settlement.from(JSON.parse(string))
        return s
      })
    )
  }

  private get _paymentInitiationMessages(): Channel {
    if (!this._paymentInitiationMessageChannel) {
      this._paymentInitiationMessageChannel = this._channel(PAYMENT_INITIATION_MESSAGE_CHANNEL)
    }
    return this._paymentInitiationMessageChannel
  }

  private get _products(): Channel {
    if (!this._productChannel) {
      this._productChannel = this._channel(PRODUCT_CHANNEL)
    }
    return this._productChannel
  }

  private get _productBrands(): Channel {
    if (!this._productBrandChannel) {
      this._productBrandChannel = this._channel(PRODUCT_BRAND_CHANNEL)
    }
    return this._productBrandChannel
  }

  private get _productTypes(): Channel {
    if (!this._productTypeChannel) {
      this._productTypeChannel = this._channel(PRODUCT_TYPE_CHANNEL)
    }
    return this._productTypeChannel
  }

  private get _productSizes(): Channel {
    if (!this._productSizeChannel) {
      this._productSizeChannel = this._channel(PRODUCT_SIZE_CHANNEL)
    }
    return this._productSizeChannel
  }

  private get _salesPeriods(): Channel {
    if (!this._salesPeriodChannel) {
      this._salesPeriodChannel = this._channel(SALES_PERIOD_CHANNEL)
    }
    return this._salesPeriodChannel
  }

  private get _sales(): Channel {
    if (!this._saleChannel) {
      this._saleChannel = this._channel(SALE_CHANNEL)
    }
    return this._saleChannel
  }

  private get _saleRows(): Channel {
    if (!this._saleRowChannel) {
      this._saleRowChannel = this._channel(SALE_ROW_CHANNEL)
    }
    return this._saleRowChannel
  }

  private get _sellers(): Channel {
    if (!this._sellerChannel) {
      this._sellerChannel = this._channel(SELLER_CHANNEL)
    }
    return this._sellerChannel
  }

  private get _settlements(): Channel {
    if (!this._settlementChannel) {
      this._settlementChannel = this._channel(SETTLEMENT_CHANNEL)
    }
    return this._settlementChannel
  }

  private _getChannelStream(name: string): Observable<any> {
    this._observedChannels.add(name)
    const subject = this._ensureSubject(name)
    this._setupChannelForwarding(name)
    return subject.asObservable()
  }

  private _ensureSubject(name: string): Subject<any> {
    if (!this._channelSubjects.has(name)) {
      this._channelSubjects.set(name, new Subject<any>())
    }
    return this._channelSubjects.get(name)
  }

  private _setupChannelForwarding(name: string): void {
    // Don't setup forwarding during cleanup
    if (this._isCleaningUp) {
      log(`[CableService] Skipping channel forwarding setup for '${name}' - cleanup in progress`)
      return
    }

    if (this._channelForwardSubscriptions.has(name)) {
      return
    }

    const subject = this._channelSubjects.get(name)
    if (!subject) {
      return
    }

    // Trigger cable creation if not already created by accessing the cable getter
    this.cable

    // IMPORTANT: If cable is already available, set up SYNCHRONOUSLY to avoid race conditions
    // where messages arrive before subscriptions are ready. Using Promise.resolve().then()
    // would delay setup to the next microtask, allowing messages to slip through.
    if (this._cable) {
      this._doSetupChannelForwarding(name, this._cable, subject)
    } else {
      // Wait for cable to be ready
      this._cableReady.pipe(take(1)).subscribe((cable) => {
        this._doSetupChannelForwarding(name, cable, subject)
      })
    }
  }

  private _doSetupChannelForwarding(name: string, cable: Cable, subject: Subject<any>): void {
    // Check again - cleanup might have started
    if (this._isCleaningUp) {
      log(`[CableService] Skipping channel forwarding setup for '${name}' - cleanup started during wait`)
      return
    }

    // If cable creation failed, cable will be null
    if (!cable) {
      warn(`[CableService] Cannot setup channel forwarding for '${name}': cable connection unavailable`)
      return
    }

    // Check if this cable is still the current one (connection might have been replaced)
    if (cable !== this._cable) {
      log(`[CableService] Skipping channel forwarding setup for '${name}' - cable was replaced`)
      return
    }

    // Check connection state - don't set up channels if we're not in a valid state
    if (this._state !== 'connecting' && this._state !== 'connected') {
      log(`[CableService] Skipping channel forwarding setup for '${name}' - state is ${this._state}`)
      return
    }

    // Check if subscription was already set up (race condition between sync and async paths)
    if (this._channelForwardSubscriptions.has(name)) {
      return
    }

    let channel: Channel
    try {
      channel = this._getOrCreateChannelByName(name)
      if (!channel) {
        return
      }

      // Guard against channel methods being undefined (race condition during cleanup)
      if (!channel.received) {
        warn(`[CableService] Channel '${name}' received() method unavailable, skipping forwarding setup`)
        return
      }
    } catch (err) {
      warn(`[CableService] Error getting channel '${name}':`, err)
      return
    }

    // Subscribe to channel lifecycle events for connection verification
    // Cast to any because TypeScript types don't include these methods, but they exist at runtime
    // Wrap in try-catch to handle ActionCable errors during disconnection
    const channelAny = channel as any

    try {
      if (channelAny.connected) {
        channelAny.connected().pipe(take(1)).subscribe(() => {
          // Guard against stale callbacks after disconnection
          if (this._state !== 'connecting' && this._state !== 'connected') return

          this._connectedChannelCount++
          if (this._connectedChannelCount === 1 && this._state === 'connecting') {
            // First channel connected = connection is verified
            clearTimeout(this._connectionTimeout)
            this._connectionTimeout = null
            this._reconnectAttempts = 0  // Reset on successful connection
            this._transitionTo('connected')
            this._healthMonitor.updateWebSocketStatus(true)
            log('[CableService] WebSocket connection verified via channel.connected()')
          }
        })
      }

      if (channelAny.rejected) {
        channelAny.rejected().pipe(take(1)).subscribe(() => {
          // Guard against stale callbacks
          if (this._state === 'disconnected' || this._state === 'reconnecting') return

          warn(`[CableService] Channel '${name}' rejected - likely auth failure`)
          clearTimeout(this._connectionTimeout)
          this._connectionTimeout = null
          this._scheduleReconnect('auth_failure')
        })
      }

      // Note: no take(1) - disconnected can happen multiple times over connection lifetime
      if (channelAny.disconnected) {
        channelAny.disconnected().subscribe(() => {
          // Guard against stale callbacks - only handle if we thought we were connected
          if (this._state !== 'connected') return

          this._connectedChannelCount = Math.max(0, this._connectedChannelCount - 1)
          if (this._connectedChannelCount === 0) {
            log('[CableService] All channels disconnected')
            this._healthMonitor.updateWebSocketStatus(false)
            this._scheduleReconnect('disconnected')
          }
        })
      }
    } catch (err) {
      warn(`[CableService] Error setting up lifecycle events for '${name}':`, err)
    }

    // Subscribe to message forwarding
    const subscription = channel.received().subscribe({
      next: (data) => {
        // Guard against race conditions during cleanup
        if (subject && !subject.closed) {
          subject.next(data)
        }
      },
      error: (err) => {
        // Guard against race conditions during cleanup
        if (subject && !subject.closed) {
          subject.error(err)
        }
      }
    })

    this._channelForwardSubscriptions.set(name, subscription)
  }

  private _getOrCreateChannelByName(name: string): Channel {
    switch (name) {
      case PAYMENT_INITIATION_MESSAGE_CHANNEL:
        return this._paymentInitiationMessages
      case PRODUCT_CHANNEL:
        return this._products
      case PRODUCT_BRAND_CHANNEL:
        return this._productBrands
      case PRODUCT_SIZE_CHANNEL:
        return this._productSizes
      case PRODUCT_TYPE_CHANNEL:
        return this._productTypes
      case SALE_CHANNEL:
        return this._sales
      case SALE_ROW_CHANNEL:
        return this._saleRows
      case SALES_PERIOD_CHANNEL:
        return this._salesPeriods
      case SELLER_CHANNEL:
        return this._sellers
      case SETTLEMENT_CHANNEL:
        return this._settlements
      default:
        return null
    }
  }

  private _channel(name: string): Channel {
    const cable = this.cable
    if (!cable) {
      warn(`[CableService] Cannot create channel '${name}' because WebSocket connection is not ready`)
      return null
    }
    const channel = cable.channel(name)
    // console.log(name + ': opened channel')
    return channel
  }

  private get cable(): Cable {
    if (!this._cable) {
      this._createCableConnection()
    }
    return this._cable
  }

  private _createCableConnection(): void {
    const currentUser = this._auth.currentUserValue

    if (!currentUser || !currentUser.token) {
      warn('[CableService] Cannot create WebSocket connection: no authentication token')
      this._cableReady.next(null) // Unblock any waiting channel subscriptions
      return
    }

    if (!currentUser.refresh_token) {
      this._auth.refreshToken().subscribe({
        next: (user) => {
          if (user && typeof user.token === 'string' && user.token.length > 0) {
            this._createCableConnectionWithToken(user.token)
          } else {
            const fallbackToken = typeof currentUser.token === 'string' ? currentUser.token : null
            if (fallbackToken) {
              this._createCableConnectionWithToken(fallbackToken)
            } else {
              this._cableReady.next(null) // Unblock waiting subscriptions on failure
            }
          }
        },
        error: (err) => {
          error('[CableService] Legacy session upgrade failed, attempting connection anyway:', err)
          const fallbackToken = typeof currentUser.token === 'string' ? currentUser.token : null
          if (fallbackToken) {
            this._createCableConnectionWithToken(fallbackToken)
          } else {
            warn('[CableService] No valid token available for WebSocket connection')
            this._cableReady.next(null) // Unblock waiting subscriptions on failure
          }
        }
      })
      return
    }

    // Check if token might be expired and proactively refresh if we have a refresh token
    if (this._isTokenLikelyExpired(currentUser.token)) {
      this._auth.refreshToken().subscribe({
        next: (user) => {
          if (user && typeof user.token === 'string' && user.token.length > 0) {
            this._createCableConnectionWithToken(user.token)
          } else {
            warn('[CableService] Token refresh did not return a usable token; skipping WebSocket connection')
            this._cableReady.next(null) // Unblock waiting subscriptions on failure
          }
        },
        error: (err) => {
          error('[CableService] Token refresh failed, attempting connection anyway:', err)
          const fallbackToken = typeof currentUser.token === 'string' ? currentUser.token : null
          if (fallbackToken) {
            this._createCableConnectionWithToken(fallbackToken)
          } else {
            warn('[CableService] No valid token available for WebSocket connection')
            this._cableReady.next(null) // Unblock waiting subscriptions on failure
          }
        }
      })
      return
    }

    const token = typeof currentUser.token === 'string' ? currentUser.token : null
    if (!token) {
      warn('[CableService] Cannot create WebSocket connection without a valid token')
      this._cableReady.next(null) // Unblock any waiting channel subscriptions
      return
    }
    this._createCableConnectionWithToken(token)
  }

  private _createCableConnectionWithToken(token: string): void {
    // Use state machine to prevent concurrent connection attempts
    if (this._state === 'connecting' || this._state === 'reconnecting') {
      log('[CableService] Connection already in progress, ignoring request')
      return
    }

    this._transitionTo('connecting')
    this._connectedChannelCount = 0

    // Include port if not standard (80/443)
    const port = location.port ? ':' + location.port : ''
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const cableUrl = protocol + '//' + location.hostname + port + '/cable'
    const params = { token: token }

    if (this._auth.isSeller) {
      params['seller_id'] = this._auth.sellerId
    }

    try {
      // CRITICAL: angular2-actioncable caches Cable objects by URL, ignoring params.
      // On reconnection with a new token, the library returns the SAME Cable with the
      // OLD token, causing auth failures and corrupted subscription state.
      // Force fresh Cable creation by clearing the cache entry.
      const cableServiceAny = this._cableService as any
      if (cableServiceAny.cables) {
        delete cableServiceAny.cables[cableUrl]
      }

      const cable = this._cableService.cable(cableUrl, params)

      // Set up WebSocket close handler for connection-level auth rejection
      // This catches auth rejection before any channel subscription completes
      const cableAny = cable as any
      if (cableAny && cableAny.connection) {
        fromEvent(cableAny.connection.webSocket || cableAny.connection, 'close').pipe(
          take(1)
        ).subscribe((event: any) => {
          // WebSocket close codes: 1008 = Policy Violation (used for auth failures)
          if (event.code === 1008 || event.reason?.includes('unauthorized')) {
            warn('[CableService] WebSocket rejected due to auth failure (code 1008)')
            clearTimeout(this._connectionTimeout)
            this._connectionTimeout = null
            this._scheduleReconnect('auth_failure')
          }
        })
      }

      this._cable = cable

      // Set up 15-second safety timeout as backup
      // Primary connection verification is via channel.connected() events in _setupChannelForwarding
      // This timeout only fires if no channel events are received (rare edge case)
      clearTimeout(this._connectionTimeout)
      this._connectionTimeout = setTimeout(() => {
        if (this._state === 'connecting') {
          warn('[CableService] No channel events after 15s - connection may have failed silently')
          this._scheduleReconnect('connection_timeout')
        }
      }, 15000)

      // Trigger channel forwarding setup immediately
      // Channel lifecycle events (connected/rejected/disconnected) will verify connection
      this._cableReady.next(cable)
      this._observedChannels.forEach(name => {
        this._channelForwardSubscriptions.delete(name)
        this._setupChannelForwarding(name)
      })

      log('[CableService] WebSocket connection initiated', {
        url: cableUrl,
        hasToken: !!params.token,
        isSeller: this._auth.isSeller,
        sellerId: this._auth.sellerId
      })
    } catch (err) {
      error('[CableService] Failed to create WebSocket connection:', err)
      this._scheduleReconnect('disconnected')
    }
  }

  // Single unified reconnection method - consolidates all reconnection logic
  private _scheduleReconnect(reason: ReconnectReason): void {
    // Prevent double reconnection
    if (this._state === 'reconnecting') {
      log(`[CableService] Already reconnecting, ignoring ${reason}`)
      return
    }

    // Don't reconnect if we're disconnected intentionally (e.g., logout)
    const sessionState = this._sessionState.currentState
    if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
      log(`[CableService] Session is ${sessionState}, not reconnecting`)
      this._transitionTo('disconnected')
      return
    }

    this._transitionTo('reconnecting')
    this._reconnectAttempts++

    if (this._reconnectAttempts > this._maxReconnectAttempts) {
      error(`[CableService] Max reconnection attempts (${this._maxReconnectAttempts}) reached`)
      this._transitionTo('disconnected')
      return
    }

    const delay = Math.min(5000 * this._reconnectAttempts, 30000)  // Exponential backoff, max 30s
    log(`[CableService] Scheduling reconnect (reason: ${reason}, attempt: ${this._reconnectAttempts}, delay: ${delay}ms)`)

    // Cancel any existing reconnect timer
    if (this._reconnectTimer) {
      this._reconnectTimer.unsubscribe()
      this._reconnectTimer = null
    }

    if (reason === 'auth_failure') {
      // Suppress tokenUpdate$ handling during auth-failure reconnection to prevent
      // dual-path race: this callback calls _doReconnect, while the refresh also
      // emits on tokenUpdate$ which would trigger _reconnectWithToken simultaneously.
      this._isAuthFailureReconnecting = true

      // Refresh token first, then reconnect
      this._refreshOrchestrator
        .requestRefresh(RefreshSource.WebSocket, 'WebSocket auth failure')
        .subscribe({
          next: (user) => {
            this._isAuthFailureReconnecting = false
            if (user && typeof user.token === 'string' && user.token.length > 0) {
              log('[CableService] Token refreshed successfully after auth failure')
              this._doReconnect(user.token)
            } else {
              warn('[CableService] Token refresh returned no token')
              this._reconnectTimer = timer(delay).subscribe(() => this._doReconnect())
            }
          },
          error: (err) => {
            this._isAuthFailureReconnecting = false
            error('[CableService] Token refresh failed:', err)
            const classification = this._errorClassifier.classify(err)
            if (classification.type === 'refresh_token_expired') {
              error('[CableService] Refresh token expired, logging out')
              this._transitionTo('disconnected')
              this._auth.logout()
            } else {
              // Retry with backoff
              this._reconnectTimer = timer(delay).subscribe(() => this._scheduleReconnect('auth_failure'))
            }
          }
        })
    } else if (reason === 'token_change') {
      // Token already changed, reconnect immediately with new token
      this._doReconnect()
    } else {
      // disconnected or connection_timeout - wait then reconnect with current token
      this._reconnectTimer = timer(delay).subscribe(() => this._doReconnect())
    }
  }

  // Helper to perform the actual reconnection
  private _doReconnect(token?: string): void {
    // Transition to disconnected BEFORE disconnect+create cycle.
    // This is critical: _scheduleReconnect sets state to 'reconnecting', but
    // _createCableConnectionWithToken guards against 'reconnecting' state.
    // Without this reset, the reconnection is permanently blocked.
    this._transitionTo('disconnected')

    this._disconnect()

    const currentUser = this._auth.currentUserValue
    const tokenToUse = token || currentUser?.token

    if (!tokenToUse) {
      warn('[CableService] Cannot reconnect: no token available')
      return
    }

    this._createCableConnectionWithToken(tokenToUse)
  }

  private _setupTokenChangeListener(): void {
    // Listen for token updates ONLY (not user data changes)
    this._tokenSubscription = this._auth.tokenUpdate$.pipe(
      // Only process when we have a token
      filter(tokens => !!tokens && !!tokens.token),
      // Short debounce to coalesce rapid updates but minimize delay
      debounceTime(100)
    ).subscribe(tokens => {
      // During auth-failure reconnection, the refresh callback handles reconnection
      // directly via _doReconnect. Ignore the tokenUpdate$ to prevent a dual-path
      // race where both paths try to reconnect simultaneously.
      if (this._isAuthFailureReconnecting) {
        log('[CableService] Ignoring token update during auth-failure reconnection')
        return
      }

      // Only reconnect if we have an existing connection and token actually changed
      if (this._cable) {
        const currentParams = (this._cable as any).params || {}
        if (currentParams.token && currentParams.token !== tokens.token) {
          log('[CableService] Token changed, reconnecting WebSocket', {
            oldTokenPrefix: currentParams.token?.substring(0, 20) + '...',
            newTokenPrefix: tokens.token?.substring(0, 20) + '...'
          })
          // Pass the new token directly to avoid race conditions
          this._reconnectWithToken(tokens.token)
        } else {
          log('[CableService] Token update received but no change detected', {
            hasCable: !!this._cable,
            hasCurrentToken: !!currentParams.token,
            tokensMatch: currentParams.token === tokens.token
          })
        }
      } else if (this._observedChannels.size > 0 && this._state !== 'connecting') {
        // Cable was lost (e.g., after a failed reconnection) but components are still
        // subscribed to channels. Re-establish the connection with the new token.
        log('[CableService] Token update received with no cable - re-establishing connection')
        this._reconnectAttempts = 0
        this._transitionTo('disconnected')
        this._createCableConnectionWithToken(tokens.token)
      } else {
        log('[CableService] Token update received but no WebSocket connection exists yet')
      }
    })
  }

  private _reconnectWithToken(token: string): void {
    if (!token) {
      warn('[CableService] Cannot reconnect: no token provided')
      return
    }

    // Only reset reconnect attempts for genuine token rotation on a healthy connection.
    // During retry cycles, the counter must be preserved so the max-attempts limit works.
    // The counter is already reset in channel.connected() on successful connection.
    if (this._state === 'connected') {
      this._reconnectAttempts = 0
    }
    if (this._reconnectTimer) {
      this._reconnectTimer.unsubscribe()
      this._reconnectTimer = null
    }
    clearTimeout(this._connectionTimeout)
    this._connectionTimeout = null

    // Transition to disconnected BEFORE calling _disconnect().
    // This prevents stale channel.disconnected() callbacks from passing the
    // 'if (this._state !== "connected") return' guard and triggering
    // _scheduleReconnect, which would set state to 'reconnecting' and
    // block _createCableConnectionWithToken.
    this._transitionTo('disconnected')

    log('[CableService] Reconnecting WebSocket with new token')
    this._disconnect()
    this._createCableConnectionWithToken(token)
  }

  private _setupVisibilityChangeListener(): void {
    // Reconnection check when tab becomes visible
    fromEvent(document, 'visibilitychange').pipe(
      debounceTime(1000) // Avoid rapid fire checks
    ).subscribe(() => {
      if (!document.hidden && this._state === 'connected') {
        // Check if WebSocket is still actually connected
        const cableAny = this._cable as any
        const isConnected = cableAny?.connection?.isOpen && cableAny.connection.isOpen()
        if (!isConnected) {
          log('[CableService] Tab visible but WebSocket disconnected, reconnecting')
          // Check if token is expired or about to expire
          const currentUser = this._auth.currentUserValue
          if (currentUser && currentUser.token && this._isTokenLikelyExpired(currentUser.token)) {
            this._scheduleReconnect('auth_failure')
          } else {
            this._scheduleReconnect('disconnected')
          }
        }
      }
    })
  }

  private _setupSessionStateListener(): void {
    // React to session state changes - disconnect when session ends
    // This is the canonical way to handle logout/expiry from any source (this tab, other tabs, etc.)
    this._sessionStateSubscription = this._sessionState.state$.pipe(
      filter(state => state === SessionState.LoggedOut || state === SessionState.Expired)
    ).subscribe(state => {
      log('[CableService] Session ended, disconnecting WebSocket', { state })
      this._transitionTo('disconnected')
      this._disconnect()
    })
  }

  private _setupPeriodicTokenRefresh(): void {
    // Refresh token every 50 minutes (before 1 hour JWT expiry)
    // This keeps the WebSocket connection alive indefinitely
    // Use 50 minutes to refresh before the 60-minute expiry
    this._tokenRefreshTimer = timer(50 * 60 * 1000, 50 * 60 * 1000).subscribe(() => {
      const currentUser = this._auth.currentUserValue
      const sessionState = this._sessionState.currentState

      // Don't refresh if no user, no refresh token, or session is logged out/expired
      if (!currentUser || !currentUser.refresh_token) {
        return
      }

      // Stop periodic refresh if session is logged out or expired
      if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
        log('[CableService] Stopping periodic refresh - session is logged out or expired')
        this._tokenRefreshTimer?.unsubscribe()
        return
      }

      log('[CableService] Periodic token refresh triggered')

      this._refreshOrchestrator
        .requestRefresh(RefreshSource.Periodic, 'Scheduled periodic refresh')
        .subscribe({
          next: (user) => {
            if (user && user.token) {
              log('[CableService] Token refreshed successfully, reconnecting WebSocket')
              this._periodicRefreshFailures = 0 // Reset failure count on success
              // Token change listener will trigger reconnection with new token
            }
          },
          error: (err) => {
            const classification = this._errorClassifier.classify(err)

            // Network and timeout errors are temporary - don't count towards logout threshold
            // Only count auth-related failures that indicate actual session problems
            if (classification.type === 'network' || classification.type === 'timeout') {
              warn('[CableService] Periodic token refresh failed due to temporary error, will retry later', {
                type: classification.type
              })
              return
            }

            error('[CableService] Periodic token refresh failed:', err)
            this._periodicRefreshFailures++

            if (classification.type === 'refresh_token_expired') {
              if (this._periodicRefreshFailures >= this._maxPeriodicRefreshFailures) {
                error('[CableService] Refresh token expired after multiple attempts, logging out')
                this._auth.logout()
              } else {
                warn(`[CableService] Periodic refresh failed (attempt ${this._periodicRefreshFailures}/${this._maxPeriodicRefreshFailures}), will retry`)
              }
            } else if (this._periodicRefreshFailures >= this._maxPeriodicRefreshFailures) {
              error('[CableService] Periodic token refresh failed multiple times, logging out')
              this._auth.logout()
            }
          }
        })
    })
  }

  private _isTokenLikelyExpired(token: string): boolean {
    try {
      // Parse JWT payload (base64 decode the middle part)
      const parts = token.split('.')
      if (parts.length !== 3) return false

      const payload = JSON.parse(atob(parts[1]))
      if (!payload.exp) return false

      // Check if token expires in less than 5 minutes (more conservative)
      const expirationTime = payload.exp * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeUntilExpiry = expirationTime - now

      // If token expires in less than 5 minutes, refresh it proactively
      return timeUntilExpiry < 300000
    } catch (err) {
      error('[CableService] Error checking token expiration:', err)
      return false
    }
  }

  private _disconnect(): void {
    // Clear any pending connection timeout
    clearTimeout(this._connectionTimeout)
    this._connectionTimeout = null

    if (this._cable) {
      // IMPORTANT: Stop WebSocket from delivering messages BEFORE cleaning up channels
      // This prevents "Cannot read properties of undefined (reading 'received')" errors
      // that occur when messages arrive during channel cleanup
      const cableAny = this._cable as any
      if (cableAny?.connection?.webSocket) {
        const ws = cableAny.connection.webSocket
        // Disable message handler to prevent any more messages from being processed
        // This is the key fix - messages can't trigger subscription notifications anymore
        ws.onmessage = null
      }
    }

    // NOW clean up channels - no more messages will be processed
    this._cleanupChannels()

    if (this._cable) {
      try {
        // Check if WebSocket is already closing/closed to avoid "closed before connection established" errors
        const cableAny = this._cable as any
        if (cableAny?.connection?.webSocket) {
          const ws = cableAny.connection.webSocket
          // Only disconnect if WebSocket is in a state where disconnecting makes sense
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            this._cable.disconnect()
          } else {
            // WebSocket is already closing or closed, just clean up
            log('[CableService] WebSocket already closing/closed, skipping disconnect')
          }
        } else {
          // No WebSocket connection yet, safe to disconnect
          this._cable.disconnect()
        }
      } catch (err) {
        // Suppress "WebSocket is closed before the connection is established" errors
        // This is expected when reconnecting quickly
        const errorMessage = err?.message || String(err)
        if (!errorMessage.includes('WebSocket is closed before the connection is established')) {
          error('[CableService] Error disconnecting cable:', err)
        }
      }
      const wasConnected = !!this._cable
      this._cable = null
      // Reset channel count since we're disconnecting all channels
      this._connectedChannelCount = 0
      // Update health monitor
      this._healthMonitor.updateWebSocketStatus(false)
      if (wasConnected) {
        log('[CableService] WebSocket disconnected')
      }
    }
  }

  private _cleanupChannels(): void {
    // Set cleanup flag to prevent new operations during cleanup
    this._isCleaningUp = true

    try {
      // First, unsubscribe from channel forwarding subscriptions
      // This prevents our RxJS subscriptions from trying to use channels that are being cleaned up
      this._channelForwardSubscriptions.forEach((subscription, name) => {
        try {
          subscription.unsubscribe()
        } catch (e) {
          warn(`[CableService] Error unsubscribing from channel forwarding for '${name}'`, e)
        }
      })
      this._channelForwardSubscriptions.clear()

      // Then unsubscribe from ActionCable channels
      // Proper lifecycle: unsubscribe -> wait for completion -> nullify
      const channels = [
        { name: 'PaymentInitiationMessage', channel: this._paymentInitiationMessageChannel },
        { name: 'Product', channel: this._productChannel },
        { name: 'ProductBrand', channel: this._productBrandChannel },
        { name: 'ProductSize', channel: this._productSizeChannel },
        { name: 'ProductType', channel: this._productTypeChannel },
        { name: 'SalesPeriod', channel: this._salesPeriodChannel },
        { name: 'Sale', channel: this._saleChannel },
        { name: 'SaleRow', channel: this._saleRowChannel },
        { name: 'Seller', channel: this._sellerChannel },
        { name: 'Settlement', channel: this._settlementChannel }
      ]

      channels.forEach(({ name, channel }) => {
        if (channel) {
          try {
            // Check if channel has unsubscribe method before calling
            if (typeof channel.unsubscribe === 'function') {
              channel.unsubscribe()
            }
          } catch (e) {
            // ActionCable might throw if channel is already cleaned up or in inconsistent state
            // This is a defensive measure for third-party library edge cases
            warn(`[CableService] Error unsubscribing from ${name} channel (may already be cleaned up)`, e)
          }
        }
      })

      // Finally, nullify channel references
      // This prevents any further attempts to use these channels
      this._paymentInitiationMessageChannel = null
      this._productChannel = null
      this._productBrandChannel = null
      this._productSizeChannel = null
      this._productTypeChannel = null
      this._salesPeriodChannel = null
      this._saleChannel = null
      this._saleRowChannel = null
      this._sellerChannel = null
      this._settlementChannel = null
    } finally {
      // Always reset cleanup flag, even if errors occurred
      this._isCleaningUp = false
    }
  }

  // Public method to manually refresh and reconnect (useful for debugging)
  public refreshAndReconnect(): void {
    this._scheduleReconnect('auth_failure')
  }

  ngOnDestroy(): void {
    // Clear all timers and subscriptions
    if (this._reconnectTimer) {
      this._reconnectTimer.unsubscribe()
    }
    if (this._tokenSubscription) {
      this._tokenSubscription.unsubscribe()
    }
    if (this._tokenRefreshTimer) {
      this._tokenRefreshTimer.unsubscribe()
    }
    if (this._sessionStateSubscription) {
      this._sessionStateSubscription.unsubscribe()
    }
    clearTimeout(this._connectionTimeout)
    this._connectionTimeout = null

    this._transitionTo('disconnected')
    this._disconnect()
  }
}